/**
 * Lazy Section Component
 * Defers rendering of below-fold sections until they enter the viewport
 * Uses IntersectionObserver with a 400px rootMargin for early loading.
 * After window load, remaining sections are stamped during idle time while the
 * user is still above them — stamping mid-scroll shifts visible content and was
 * measured at up to 0.92 CLS on collection pages (GSC « Problème CLS > 0,1 mobile »).
 */
(function() {
  'use strict';

  if (customElements.get('lazy-section')) return;

  var pending = [];

  function stampNextIdle() {
    var el = pending.shift();
    while (el && (el._loaded || !el.isConnected)) el = pending.shift();
    if (!el) return;
    el._loadNow();
    // One section per idle slice to avoid a single long task
    if (pending.length) requestIdle(stampNextIdle);
  }

  function requestIdle(fn) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(fn, { timeout: 2000 });
    } else {
      setTimeout(fn, 200);
    }
  }

  // Trigger at DOMContentLoaded, not window.load: on the live site the load
  // event can arrive after 10s+ (3rd parties, images lancées par le scroll),
  // long after early-scrolling users have reached the collapsed sections.
  if (document.readyState !== 'loading') {
    requestIdle(stampNextIdle);
  } else {
    document.addEventListener('DOMContentLoaded', function() { requestIdle(stampNextIdle); });
  }

  customElements.define('lazy-section', class extends HTMLElement {
    constructor() {
      super();
      this._loaded = false;
    }

    connectedCallback() {
      // Skip lazy loading in theme editor for better editing experience
      if (Shopify.designMode) {
        this._loadContent();
        return;
      }

      // Use IntersectionObserver for lazy loading
      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(
          this._onIntersect.bind(this),
          { rootMargin: '400px 0px', threshold: 0 }
        );
        this._observer.observe(this);
        pending.push(this);
      } else {
        // Fallback for older browsers
        this._loadContent();
      }
    }

    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }

    _onIntersect(entries) {
      if (entries[0].isIntersecting && !this._loaded) {
        this._loadNow();
      }
    }

    _loadNow() {
      if (this._loaded) return;
      this._loaded = true;
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      this._loadContent();
    }

    _loadContent() {
      const template = this.querySelector('template');
      if (!template) return;

      // Clone template content and append to the section
      const content = template.content.cloneNode(true);
      template.replaceWith(content);

      // Dispatch event for any scripts that need to initialize
      this.dispatchEvent(new CustomEvent('lazy-section:loaded', { bubbles: true }));
    }
  });
})();
